import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  HStack,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useAtomValue } from 'jotai';
import {
  Mail,
  MessageSquare,
  MoreVertical,
  Settings,
  LogOut,
  UserPlus,
  Users,
} from 'lucide-react';
import { useAuth, useFamily, useInvites, useLoadingState } from '../hooks';
import { LoadingPanel } from '../components/brand/TwindenLoader';
import { FamilyAvatar } from '../components/family/FamilyAvatar';
import { ConfirmSheet } from '../components/ConfirmSheet';
import { InvitePeopleSheet } from '../components/family/InvitePeopleSheet';
import { joinRequestCountsAtom } from '../store';
import { PageScaffold, Section } from '../components/page/PageScaffold';
import { ApiError, type FamilyListItem } from '../lib/api';
import { useT, type MessageKey } from '../i18n';

const ROLE_LABEL: Record<FamilyListItem['role'], MessageKey> = {
  root: 'account.role.root',
  admin: 'account.role.admin',
  member: 'account.role.member',
};

export function FamilyPage() {
  const {
    families,
    loading,
    error,
    loadFamilies,
    setCurrentFamilyId,
    removeMember,
    forgetFamily,
  } = useFamily();
  // The "You're invited" card (D351) needs its own fetch: each useInvites
  // keeps its own list.
  const { count: inviteCount, refresh: refreshInvites } = useInvites();
  useEffect(() => {
    void refreshInvites();
  }, [refreshInvites]);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  // The mark while the families load; the list from before shows at once
  // (M66, D248).
  const familiesState = useLoadingState({
    loading,
    hasData: families.length > 0,
    error,
    retry: () => void loadFamilies(),
  });
  const t = useT();

  const [leaving, setLeaving] = useState<FamilyListItem | null>(null);
  // Every member can share the family link (D152).
  const [inviting, setInviting] = useState<FamilyListItem | null>(null);
  const requestCounts = useAtomValue(joinRequestCountsAtom);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    loadFamilies();
  }, [loadFamilies]);

  const openChat = (familyId: string) => {
    setCurrentFamilyId(familyId);
    navigate('/');
  };

  const confirmLeave = async () => {
    if (!leaving || !currentUser) return;
    try {
      await removeMember(leaving.id, currentUser.id);
      forgetFamily(leaving.id);
      toast({
        title: t('account.families.left', { name: leaving.name }),
        status: 'success',
      });
    } catch (err) {
      toast({
        title: t('account.families.leaveFailed'),
        description:
          err instanceof ApiError
            ? err.message
            : t('account.profile.unknownError'),
        status: 'error',
      });
    } finally {
      setLeaving(null);
      onClose();
    }
  };

  return (
    <PageScaffold
      title={t('account.profile.families')}
      subtitle={t('account.families.subtitle')}
    >
      {inviteCount > 0 && (
        <HStack
          as="button"
          type="button"
          w="full"
          mb={3}
          spacing={3}
          p={4}
          borderRadius="card"
          bg="accent.tint"
          color="accent"
          textAlign="left"
          onClick={() => navigate('/invites')}
        >
          <Mail size={20} aria-hidden="true" />
          <Text flex="1" fontWeight={700} fontSize="16px">
            {inviteCount === 1
              ? t('account.families.invitedOne')
              : t('account.families.invitedOther', { count: inviteCount })}
          </Text>
          <Text fontWeight={700}>{t('account.families.review')}</Text>
        </HStack>
      )}

      {error && (
        <Alert status="error" borderRadius="xl" mb={3}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {familiesState.show ? (
        <LoadingPanel state={familiesState} />
      ) : families.length === 0 ? (
        <Section>
          <VStack spacing={3} py={6} textAlign="center">
            <Users size={36} aria-hidden="true" />
            <Heading as="h2" fontSize="22px">
              {t('account.families.emptyTitle')}
            </Heading>
            <Text color="ink.body" maxW="md">
              {t('account.families.emptyBody')}
            </Text>
            <Button
              variant="outline"
              colorScheme="gray"
              borderColor="line"
              onClick={() => navigate('/invites')}
            >
              {t('account.families.checkInvites')}
            </Button>
          </VStack>
        </Section>
      ) : (
        <VStack align="stretch" spacing="10px">
          {families.map((family) => (
            <Box
              key={family.id}
              bg="surface"
              borderWidth="1px"
              borderColor="line"
              borderRadius="card"
              p={4}
            >
              <HStack spacing={3}>
                <HStack
                  as="button"
                  type="button"
                  flex="1"
                  minW={0}
                  spacing={3}
                  textAlign="left"
                  onClick={() => openChat(family.id)}
                  aria-label={t('account.families.open', { name: family.name })}
                >
                  {family.avatarUrl ? (
                    <FamilyAvatar
                      id={family.id}
                      name={family.name}
                      src={family.avatarUrl}
                      size="md"
                    />
                  ) : (
                    <Center
                      w="48px"
                      h="48px"
                      borderRadius="16px"
                      bg="accent.solid"
                      color="accent.onSolid"
                      fontFamily="heading"
                      fontWeight={800}
                      fontSize="20px"
                      flexShrink={0}
                      aria-hidden="true"
                    >
                      {family.name.charAt(0).toUpperCase()}
                    </Center>
                  )}
                  <Box minW={0}>
                    <Text fontWeight={700} fontSize="17px" noOfLines={1}>
                      {family.name}
                    </Text>
                    <Text fontSize="14px" color="muted" noOfLines={1}>
                      {t(ROLE_LABEL[family.role])} ·{' '}
                      {family.memberCount === 1
                        ? t('account.switcher.membersOne')
                        : t('account.switcher.membersOther', {
                            count: family.memberCount,
                          })}
                    </Text>
                  </Box>
                </HStack>
                <Menu placement="bottom-end">
                  <MenuButton
                    as={IconButton}
                    icon={<MoreVertical size={20} />}
                    aria-label={t('account.families.actions')}
                    variant="ghost"
                    colorScheme="gray"
                  />
                  <MenuList>
                    <MenuItem
                      icon={<MessageSquare size={16} />}
                      onClick={() => openChat(family.id)}
                    >
                      {t('account.families.openChats')}
                    </MenuItem>
                    <MenuItem
                      icon={<UserPlus size={16} />}
                      onClick={() => setInviting(family)}
                    >
                      {t('account.invite.menu')}
                    </MenuItem>
                    {family.role !== 'member' && (
                      <MenuItem
                        icon={<Settings size={16} />}
                        onClick={() => navigate(`/family/${family.id}/admin`)}
                      >
                        {t('account.families.manage')}
                      </MenuItem>
                    )}
                    <MenuItem
                      icon={<LogOut size={16} />}
                      color="danger"
                      onClick={() => {
                        setLeaving(family);
                        onOpen();
                      }}
                    >
                      {t('account.families.leave')}
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
              {/* Under the name, so a long family name isn't squeezed. */}
              {(requestCounts[family.id] ?? 0) > 0 && (
                <Button
                  size="sm"
                  bg="saffron"
                  color="#1F1D1B"
                  _hover={{ opacity: 0.9 }}
                  mt={3}
                  ml="60px"
                  onClick={() =>
                    navigate(`/family/${family.id}/admin?tab=requests`)
                  }
                >
                  {t('account.requests.asking', {
                    count: requestCounts[family.id] ?? 0,
                  })}
                </Button>
              )}
            </Box>
          ))}
        </VStack>
      )}

      {inviting && (
        <InvitePeopleSheet
          isOpen
          onClose={() => setInviting(null)}
          familyId={inviting.id}
          familyName={inviting.name}
          canManage={inviting.role !== 'member'}
        />
      )}

      <ConfirmSheet
        centered
        isOpen={isOpen}
        onClose={onClose}
        icon={<LogOut size={28} />}
        title={t('account.families.leaveTitle', {
          name: leaving?.name ?? t('account.families.thisFamily'),
        })}
        body={t('account.families.leaveBody')}
        confirmLabel={t('account.families.leave')}
        cancelLabel={t('account.families.stay')}
        onConfirm={() => void confirmLeave()}
      />
    </PageScaffold>
  );
}
